import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Search, ChevronsUpDown } from "lucide-react";

interface GamePickerProps {
  games: any[] | undefined;
  selectedSlug: string;
  onSelect: (slug: string) => void;
  label?: string;
}

export function GamePicker({ games, selectedSlug, onSelect, label = "Select Game" }: GamePickerProps) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);

  const selected = games?.find((g: any) => g.slug === selectedSlug);
  const filtered = games?.filter((g: any) => !search || g.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div>
      <Label>{label}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" role="combobox" className="w-full justify-between font-normal">
            {selected ? `${selected.name}${selected.states ? ` (${selected.states.abbreviation})` : ''}` : "Search for a game..."}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
          <div className="p-2 border-b">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                aria-label="Search games"
                placeholder="Search games..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 h-9"
              />
            </div>
          </div>
          <div className="max-h-60 overflow-y-auto p-1">
            {filtered?.map((g: any) => (
              <button
                key={g.id}
                onClick={() => { onSelect(g.slug); setOpen(false); setSearch(""); }}
                className={`flex items-center w-full rounded-sm px-2 py-1.5 text-sm hover:bg-accent transition-colors text-left ${selectedSlug === g.slug ? 'bg-accent font-medium' : ''}`}
              >
                {g.name}{g.states ? ` (${g.states.abbreviation})` : ''}
              </button>
            ))}
            {filtered?.length === 0 && (
              <p className="text-sm text-muted-foreground p-2 text-center">No games found</p>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
